import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { overlayRoot, useOverlay } from '../../lib/overlay'
import { accountMenuData, signOut } from '../../lib/accountMenu'

/**
 * The header's search field and the overlay behind it.
 *
 * One component rather than two because the field *is* the door: it never
 * takes a keystroke itself, it opens the palette, and the palette is where the
 * typing happens. That is what lets ⌘K from anywhere and a tap on the phone
 * bar's accent key land in exactly the same place.
 *
 * Talks to /search on the terms the old nav's own field used before it was
 * deleted: two characters minimum, 150ms debounce, four groups in a fixed
 * order.
 */

interface SearchHit {
  id: number
  title: string
  detail: string | null
  /** A word for the state that makes this hit not quite live — "Archived", "Cancelled". */
  badge: string | null
  url: string
}

/**
 * Something the palette *does* rather than somewhere it goes.
 *
 * There is one so far and it is the reason the palette has any: signing out.
 * Round two's `1a` puts it here as well as in the account menu, because a
 * control needed on a borrowed machine has to be reachable without finding a
 * 28px avatar with the pointer.
 */
interface Command {
  key: string
  label: string
  note?: string
  run: () => void
}

type GroupKey = 'projects' | 'clients' | 'people' | 'contracts'

type SearchResults = Record<GroupKey, SearchHit[]>

const EMPTY: SearchResults = { projects: [], clients: [], people: [], contracts: [] }

// Iteration order, so also the order the groups appear in. Contracts last: the
// group reached for by name least often, and the one that comes back empty for
// anyone without finance access.
const GROUP_LABELS: Record<GroupKey, string> = {
  projects: 'Projects',
  clients: 'Clients',
  people: 'People',
  contracts: 'Contracts',
}

/**
 * What the palette can do, as opposed to where it can go.
 *
 * Read off the account menu's own props (lib/accountMenu.tsx), so the address
 * shown beside "Sign out" here and the one in the menu cannot disagree — they
 * are the same string from the same place.
 */
function availableCommands(): Command[] {
  const account = accountMenuData()
  if (!account) return []

  return [
    {
      key: 'sign-out',
      label: account.viewing ? 'Sign out of Wingspace' : 'Sign out',
      note: account.email,
      run: () => signOut(),
    },
    {
      key: 'sign-out-others',
      label: 'Sign out other devices',
      // Routes rather than acting: ending somebody else's session from a
      // fuzzy match on three letters is not a thing to do without looking at
      // the list first.
      note: 'Settings',
      run: () => window.location.assign('/settings/account?sheet=devices'),
    },
  ]
}

// Matches SearchesController::MINIMUM_QUERY_LENGTH. Asking earlier spends a
// request to be told what the server would refuse to answer.
/**
 * Somewhere the palette *goes* — one of the top nav's destinations.
 *
 * Read straight off `#app-nav` rather than listed here, for the same reason
 * the sign-out command reads the account menu's props: two lists of the same
 * destinations drift, and this one has to match what `app_nav_links` decided,
 * including Invoices dropping out for a member without finance access.
 *
 * The nav is server-rendered and Turbo-permanent, and it stays in the document
 * at every width — below 48rem it is `display:none` and the phone tab bar
 * shows instead, and between 48rem and 68.75rem its fifth link onwards is
 * hidden. Those are exactly the widths where Clients, People and Studio have
 * no other door, which is what this is for.
 */
interface Destination {
  key: string
  label: string
  url: string
}

function availableDestinations(): Destination[] {
  return Array.from(document.querySelectorAll<HTMLAnchorElement>('#app-nav a')).map((link) => ({
    key: link.getAttribute('href') ?? link.textContent ?? '',
    label: (link.textContent ?? '').trim(),
    url: link.getAttribute('href') ?? '',
  }))
}

const MINIMUM_QUERY_LENGTH = 2
const DEBOUNCE_MS = 150

export function CommandPalette() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return
      event.preventDefault()
      setOpen(true)
    }

    // The phone tab bar's accent key is ERB, and this is how it reaches in.
    function onClick(event: MouseEvent) {
      if ((event.target as HTMLElement)?.closest?.('[data-app-search-open]')) setOpen(true)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', onClick)
    }
  }, [])

  return (
    <>
      <button
        type="button"
        className="tab"
        onClick={() => setOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '0 10px 0 12px',
          height: 32,
          borderRadius: 8,
          border: '1px solid var(--w-line)',
          background: 'var(--w-sunk)',
          // `width` with a floor rather than `minWidth: 210`: a minimum is a
          // promise the header cannot keep at 768px, where everything else in
          // that row is fixed and this is the only thing that can give. A
          // min-width also silently beat the `max-width: 100%` meant to rein
          // it in, so the header laid out as if this were 155px wide while it
          // drew 210 — 55px of overlap waiting for a narrower case.
          width: 210,
          minWidth: 0,
          maxWidth: '100%',
          cursor: 'pointer',
          flex: '0 1 auto',
        }}
      >
        <Magnifier colour="var(--color-muted)" />
        <span
          style={{
            flex: 1,
            textAlign: 'left',
            font: '400 12px Manrope,sans-serif',
            color: 'var(--color-muted)',
            whiteSpace: 'nowrap',
          }}
        >
          Search everything
        </span>
        <span
          className="text-data"
          style={{
            padding: '2px 5px',
            borderRadius: 4,
            background: 'var(--w-fill)',
            color: 'var(--color-muted)',
            fontSize: 11,
          }}
        >
          ⌘K
        </span>
      </button>

      <Palette open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function Palette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const panel = useOverlay(open, onClose)
  const root = overlayRoot()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResults>(EMPTY)
  const [active, setActive] = useState(-1)
  const input = useRef<HTMLInputElement>(null)

  // A palette that reopens holding the last search is showing an answer to a
  // question nobody just asked.
  useEffect(() => {
    if (open) {
      setQuery('')
      setResults(EMPTY)
      setActive(-1)
      // After useOverlay has moved focus to the panel, which it does first.
      input.current?.focus()
    }
  }, [open])

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < MINIMUM_QUERY_LENGTH) {
      setResults(EMPTY)
      return
    }

    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`/search?q=${encodeURIComponent(trimmed)}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        })
        if (!response.ok) return
        setResults(await response.json())
        setActive(-1)
      } catch {
        // An aborted request is the expected outcome of typing another letter.
      }
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query])

  if (!open || !root) return null

  // Matched here rather than on the server: a command is a fixed short list,
  // and asking the network whether "sign" matches "Sign out" would put a round
  // trip in front of the one thing this is for.
  const needle = query.trim().toLowerCase()
  const commands = needle.length > 0 ? availableCommands().filter((one) => one.label.toLowerCase().includes(needle)) : []

  // Unfiltered on an empty query, so opening the palette with nothing typed
  // *is* the navigation menu. That is the whole answer for a phone, where the
  // tab bar carries four of the seven destinations and the rest have nowhere
  // else to be reached from.
  const destinations = availableDestinations().filter(
    (one) => needle.length === 0 || one.label.toLowerCase().includes(needle)
  )

  const groups = (Object.keys(GROUP_LABELS) as GroupKey[])
    .map((key) => ({ key, hits: results[key] ?? [] }))
    .filter((group) => group.hits.length > 0)
  // Commands first: they are the exact answer where a search hit is a guess.
  const flat = [
    ...commands.map((one) => ({ id: 0, title: one.label, detail: null, badge: null, url: '', run: one.run })),
    ...destinations.map((one) => ({ id: 0, title: one.label, detail: null, badge: null, url: one.url })),
    ...groups.flatMap((group) => group.hits),
  ]

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((i) => (flat.length === 0 ? -1 : (i + 1) % flat.length))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((i) => (flat.length === 0 ? -1 : (i <= 0 ? flat.length : i) - 1))
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault()
      const chosen = flat[active] as SearchHit & { run?: () => void }
      if (chosen.run) chosen.run()
      else window.location.assign(chosen.url)
    }
  }

  let index = -1

  return createPortal(
    <>
      <div
        className="tab"
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--w-scrim)',
          zIndex: 30,
          cursor: 'default',
        }}
      />
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Search everything"
        className="fr"
        style={{
          position: 'absolute',
          top: 96,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(560px,calc(100% - 36px))',
          borderRadius: 16,
          border: '1px solid var(--w-line-strong)',
          background: 'var(--w-sheet)',
          boxShadow: 'var(--w-float)',
          overflow: 'hidden',
          outline: 'none',
          zIndex: 31,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            padding: '15px 16px',
            borderBottom: '1px solid var(--w-line)',
          }}
        >
          <Magnifier colour="var(--color-action-ink)" size={15} />
          <input
            ref={input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search projects, clients, people…"
            aria-label="Search"
            style={{
              flex: 1,
              minWidth: 0,
              border: 0,
              background: 'transparent',
              padding: 0,
              font: '500 14px Manrope,sans-serif',
              color: 'var(--color-text)',
              outline: 'none',
            }}
          />
          <span
            className="text-data"
            style={{
              padding: '2px 6px',
              borderRadius: 5,
              background: 'var(--w-fill)',
              color: 'var(--color-muted)',
              fontSize: 11,
            }}
          >
            esc
          </span>
        </div>

        <div style={{ padding: '9px 0', maxHeight: 'min(58vh,420px)', overflowY: 'auto' }}>
          {commands.length > 0 && (
            <div>
              <div
                style={{
                  padding: '5px 16px 7px',
                  font: '600 11px Manrope,sans-serif',
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  color: 'var(--color-muted)',
                }}
              >
                Commands
              </div>
              {commands.map((command) => {
                index += 1
                const on = index === active
                return (
                  <button
                    key={command.key}
                    type="button"
                    className="tab"
                    onMouseEnter={() => setActive(index)}
                    onClick={command.run}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      width: '100%',
                      height: 34,
                      padding: '0 16px',
                      border: 0,
                      textAlign: 'left',
                      cursor: 'pointer',
                      background: on ? 'var(--w-fill)' : 'transparent',
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        font: '600 12px Manrope,sans-serif',
                        color: 'var(--color-amber-ink)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {command.label}
                    </span>
                    {command.note && (
                      <span
                        className="text-data"
                        style={{ fontSize: 10, color: 'var(--color-faint)', flex: 'none' }}
                      >
                        {command.note}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {destinations.length > 0 && (
            <div>
              <div
                style={{
                  padding: '5px 16px 7px',
                  font: '600 11px Manrope,sans-serif',
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  color: 'var(--color-muted)',
                }}
              >
                Go to
              </div>
              {destinations.map((destination) => {
                index += 1
                const on = index === active
                return (
                  <a
                    key={destination.key}
                    href={destination.url}
                    className="tab"
                    onMouseEnter={() => setActive(index)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      height: 34,
                      padding: '0 16px',
                      background: on ? 'var(--w-pane-hi)' : 'transparent',
                      borderLeft: on ? '2px solid var(--color-accent)' : '2px solid transparent',
                      color: 'inherit',
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        font: '600 12px Manrope,sans-serif',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {destination.label}
                    </span>
                    {on && (
                      <span className="text-data" style={{ fontSize: 11, color: 'var(--color-muted)' }}>
                        ↵
                      </span>
                    )}
                  </a>
                )
              })}
            </div>
          )}

          {groups.length === 0 && commands.length === 0 && destinations.length === 0 ? (
            <p
              style={{
                margin: 0,
                padding: '14px 16px',
                font: '400 12px/1.6 Manrope,sans-serif',
                color: 'var(--color-muted)',
              }}
            >
              {query.trim().length < MINIMUM_QUERY_LENGTH
                ? 'Type at least two letters to search projects, clients, people and contracts.'
                : `Nothing matching “${query.trim()}”.`}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.key}>
                <div
                  style={{
                    padding: '5px 16px 7px',
                    font: '600 11px Manrope,sans-serif',
                    letterSpacing: '.14em',
                    textTransform: 'uppercase',
                    color: 'var(--color-muted)',
                  }}
                >
                  {GROUP_LABELS[group.key]}
                </div>
                {group.hits.map((hit) => {
                  index += 1
                  const on = index === active
                  return (
                    <a
                      key={`${group.key}-${hit.id}`}
                      href={hit.url}
                      className="tab"
                      onMouseEnter={() => setActive(flat.indexOf(hit))}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 11,
                        padding: '9px 16px',
                        background: on ? 'var(--w-pane-hi)' : 'transparent',
                        borderLeft: on ? '2px solid var(--color-accent)' : '2px solid transparent',
                        color: 'inherit',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            font: '600 13px Manrope,sans-serif',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {hit.title}
                        </div>
                        {(hit.detail || hit.badge) && (
                          <div style={{ font: '400 11px Manrope,sans-serif', color: 'var(--color-muted)' }}>
                            {[hit.badge, hit.detail].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                      {on && (
                        <span className="text-data" style={{ fontSize: 11, color: 'var(--color-muted)' }}>
                          ↵
                        </span>
                      )}
                    </a>
                  )
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </>,
    root
  )
}

function Magnifier({ colour, size = 12 }: { colour: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={size > 12 ? 1.7 : 1.6}
      style={{ color: colour, flex: 'none' }}
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5 14 14" />
    </svg>
  )
}
